import { SettingsContentCard } from '__components/Card/Card';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { Header, Title } from './styles';
import { SearchPill } from '@kizen/kds/SearchPill';
import { useTranslation } from 'react-i18next';
import { COLUMN_SIZE, Grid } from '@kizen/kds/Grid';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Tile } from './components/Tile';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import PluginService from 'services/PluginService';
import { PLUGINS } from 'queries/query-keys';
import { MarketplacePlugin } from './types';
import { PluginContext } from 'ts-components/Plugins/context';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import Loader from '__components/Kizen/Loader';
import { useSelector } from 'react-redux';
import { Typography } from '@kizen/kds/Typography';
import { useSetTitleOnLoad } from '__hooks/useSetTitleOnLoad';
import { getManageIntegrationSecrets } from 'store/authentication/selectors';
import { EditIntegrationSecretModal } from '__pages/Settings/pages/ManageConnections/IntegrationSecrets/IntegrationSecretModal';
import { NoPermissionModal } from './components/Tile/NoPermissionModal';
import { Spacer } from '@kizen/kds/Spacer';
import { SetupAssistant } from './components/SetupAssistant';
import { usePluginEnable } from './Details/usePluginEnable';
import TablePagination from '__components/Tables/Big/TablePagination';
import { AbsoluteLoader } from './components/SetupAssistant/styles';
import { useMarketplacePermissions } from './permissions';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';

// Should be divisible by 3 since we do 3 columns
const PLUGINS_PER_PAGE = 12;

const defaultPlugins = ['google_ads', 'gmail_inbox', 'outlook_inbox'];

const MarketplacePage = () => {
  const { t } = useTranslation();

  useSetTitleOnLoad(t('App Marketplace'));

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const { canEditPlugins } = useMarketplacePermissions();

  const {
    data: pluginsData,
    isLoading: isAvailableSectionLoading,
    isRefetching: isAvailableSectionRefetching,
  } = useQuery<{
    results: MarketplacePlugin[];
    count: number;
  }>({
    queryFn: () =>
      PluginService.getPluginApps(searchTerm, false, page, PLUGINS_PER_PAGE),
    queryKey: PLUGINS.GET_PLUGIN_APPS(
      searchTerm,
      false,
      page,
      PLUGINS_PER_PAGE
    ),
    placeholderData: keepPreviousData,
    enabled: canEditPlugins,
  });

  const { data: mockedPluginsData, isLoading: isMockedPluginsLoading } =
    useQuery<MarketplacePlugin[]>({
      queryFn: async () => {
        const result = await Promise.all(
          defaultPlugins.map((apiName) => PluginService.getPluginApp(apiName))
        );

        return result;
      },
      queryKey: PLUGINS.GET_MOCKED_PLUGIN_APPS,
    });

  const manageIntegrationSecretsAccess = useSelector(
    getManageIntegrationSecrets
  );

  const { data: businessPluginApps, isLoading } = useBusinessPluginApps(true);

  const businessPluginAppsByApiName = useMemo(() => {
    return (
      businessPluginApps?.reduce((acc: any, curr: any) => {
        return {
          ...acc,
          [curr.plugin_app.api_name]: curr,
        };
      }, {}) ?? {}
    );
  }, [businessPluginApps]);

  const { plugins: installedPlugins, refreshPluginArtifacts } =
    useContext(PluginContext);

  const refetchAll = useCallback(
    (pluginApiName?: string) => refreshPluginArtifacts(pluginApiName),
    [refreshPluginArtifacts]
  );

  const {
    onChangeEnabled,
    secretsToEdit,
    setupAssistantConfig,
    pendingChange,
    noPermissionModalProps,
    setupAssistantPending,
    handleCloseSecretsInlineModal,
    handleCloseSetupAssistant,
    onChangeSetupAssistantConfig,
    onImportKZNFileStep,
  } = usePluginEnable({
    businessPluginAppsByApiName,
    refetch: refetchAll,
  });

  const onChangeConfig = useCallback(
    async (pluginApiName: string, config: any) => {
      try {
        await PluginService.updateBusinessPluginApp(pluginApiName, {
          config,
        });
        await refetchAll(pluginApiName);
      } catch (ex) {
        monitoringExceptionHelper(ex);
      }
    },
    [refetchAll]
  );

  const installedPluginsGridContent = useMemo(() => {
    if (!businessPluginApps) {
      return [];
    }

    const plugins: MarketplacePlugin[] = businessPluginApps
      .filter((p: any) => {
        return p.disabled === false;
      })
      .map((p: any) => {
        return p.plugin_app;
      });

    const consideredPlugins = [...plugins, ...(mockedPluginsData || [])];

    const res = consideredPlugins
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      })
      .reduce((acc, curr) => {
        const res = [...acc];
        const last = res[res.length - 1];

        if (!last || last.content.length === 3) {
          res.push({
            id: `row-${res.length}`,
            content: [],
            layout: [],
          });
        }

        const installation = installedPlugins?.[curr.api_name];
        const existingBusinessPluginApp =
          businessPluginAppsByApiName[curr.api_name];

        res[res.length - 1].layout.push(COLUMN_SIZE.THIRD_WIDTH);
        res[res.length - 1].content.push(
          <Tile
            key={curr.id}
            plugin={curr}
            installation={installation}
            onChangeEnabled={onChangeEnabled}
            onChangeConfig={onChangeConfig}
            overrideCheckedState={
              pendingChange.apiName === curr.api_name
                ? pendingChange.enabled
                : undefined
            }
            hasBusinessPluginApp={Boolean(existingBusinessPluginApp)}
            businessPluginApp={existingBusinessPluginApp}
            qa={{
              'plugin-id': curr.id,
              'plugin-name': curr.api_name,
            }}
          />
        );

        return res;
      }, [] as any);

    return res;
  }, [
    installedPlugins,
    onChangeEnabled,
    pendingChange,
    businessPluginAppsByApiName,
    onChangeConfig,
    businessPluginApps,
    mockedPluginsData,
  ]);

  const previousPluginsGridContent = useMemo(() => {
    if (!businessPluginApps) {
      return [];
    }

    const plugins: MarketplacePlugin[] = businessPluginApps
      .filter((p: any) => {
        return p.disabled === true;
      })
      .map((p: any) => {
        return p.plugin_app;
      });

    const res = plugins
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      })
      .reduce((acc, curr) => {
        const res = [...acc];
        const last = res[res.length - 1];

        if (!last || last.content.length === 3) {
          res.push({
            id: `row-${res.length}`,
            content: [],
            layout: [],
          });
        }

        const installation = installedPlugins?.[curr.api_name];
        const existingBusinessPluginApp =
          businessPluginAppsByApiName[curr.api_name];

        res[res.length - 1].layout.push(COLUMN_SIZE.THIRD_WIDTH);
        res[res.length - 1].content.push(
          <Tile
            key={curr.id}
            plugin={curr}
            installation={installation}
            onChangeEnabled={onChangeEnabled}
            onChangeConfig={onChangeConfig}
            overrideCheckedState={
              pendingChange.apiName === curr.api_name
                ? pendingChange.enabled
                : undefined
            }
            hasBusinessPluginApp={Boolean(existingBusinessPluginApp)}
            qa={{
              'plugin-id': curr.id,
              'plugin-name': curr.api_name,
            }}
          />
        );

        return res;
      }, [] as any);

    return res;
  }, [
    installedPlugins,
    onChangeEnabled,
    pendingChange,
    businessPluginAppsByApiName,
    onChangeConfig,
    businessPluginApps,
  ]);

  const marketplaceGridContent = useMemo(() => {
    if (!pluginsData?.results) {
      return [];
    }

    const res = pluginsData.results
      .sort((a, b) => {
        return a.name > b.name ? 1 : -1;
      })
      .reduce((acc, curr) => {
        const res = [...acc];
        const last = res[res.length - 1];

        if (!last || last.content.length === 3) {
          res.push({
            id: `row-${res.length}`,
            content: [],
            layout: [],
          });
        }

        const installation = installedPlugins?.[curr.api_name];
        const existingBusinessPluginApp =
          businessPluginAppsByApiName[curr.api_name];

        res[res.length - 1].layout.push(COLUMN_SIZE.THIRD_WIDTH);
        res[res.length - 1].content.push(
          <Tile
            key={curr.id}
            plugin={curr}
            installation={installation}
            onChangeEnabled={onChangeEnabled}
            onChangeConfig={onChangeConfig}
            overrideCheckedState={
              pendingChange.apiName === curr.api_name
                ? pendingChange.enabled
                : undefined
            }
            hasBusinessPluginApp={Boolean(existingBusinessPluginApp)}
            qa={{
              'plugin-id': curr.id,
              'plugin-name': curr.api_name,
            }}
          />
        );

        return res;
      }, [] as any);

    return res;
  }, [
    pluginsData,
    installedPlugins,
    onChangeEnabled,
    pendingChange,
    businessPluginAppsByApiName,
    onChangeConfig,
  ]);

  return (
    <PageSizing>
      <ContentWidth>
        <Header>
          <div />
          <Title>{t('App Marketplace')}</Title>
        </Header>
      </ContentWidth>
      <SettingsContentCard>
        {isLoading || isMockedPluginsLoading ? (
          <Loader loading />
        ) : (
          <>
            <div data-qa-section="installed-apps">
              <Typography size="xl" weight="semibold" variant="header">
                {t('Installed Apps')}
              </Typography>

              <Spacer size={20} />

              {installedPluginsGridContent.length > 0 ? (
                <Grid gap={20} config={installedPluginsGridContent} />
              ) : (
                <Typography className="text-center">
                  {t('No Apps Found')}
                </Typography>
              )}
            </div>

            {previousPluginsGridContent.length > 0 && (
              <div data-qa-section="previously-installed-apps">
                <Spacer size={30} />

                <Typography size="xl" weight="semibold" variant="header">
                  {t('Previously Installed Apps')}
                </Typography>

                <Spacer size={20} />

                <Grid gap={20} config={previousPluginsGridContent} />
              </div>
            )}

            <Spacer size={30} />

            <div data-qa-section="available-apps">
              <Typography size="xl" weight="semibold" variant="header">
                {t('Available Apps')}
              </Typography>

              <Spacer size={20} />
              <SearchPill
                value={searchTerm}
                onChange={(term) => {
                  setSearchTerm(term);

                  // If the search term changes, go back to the first page
                  if (term !== searchTerm) {
                    setPage(1);
                  }
                }}
                placeholder={t('Search Marketplace')}
                inline
              />
              <Spacer size={20} />

              <div className="min-h-[75px] relative">
                {marketplaceGridContent.length > 0 ? (
                  <>
                    <Grid gap={20} config={marketplaceGridContent} />
                    <Spacer size={20} />
                    <TablePagination
                      page={page}
                      onChangePage={setPage}
                      perPage={PLUGINS_PER_PAGE}
                      showPageSizeOptions={false}
                      totalCount={pluginsData?.count}
                    />
                  </>
                ) : (
                  <div className="pt-12">
                    <Typography className="text-center">
                      {t('No Apps Found')}
                    </Typography>
                  </div>
                )}
                {isAvailableSectionLoading || isAvailableSectionRefetching ? (
                  <AbsoluteLoader loading transparent inline />
                ) : null}
              </div>
            </div>
          </>
        )}
      </SettingsContentCard>
      {setupAssistantPending && setupAssistantConfig ? (
        <SetupAssistant
          onClose={handleCloseSetupAssistant}
          config={setupAssistantConfig}
          onChangeConfig={onChangeSetupAssistantConfig}
          businessPluginAppsByApiName={businessPluginAppsByApiName}
          onImportKZNFileStep={onImportKZNFileStep}
        />
      ) : null}
      {manageIntegrationSecretsAccess.edit &&
      secretsToEdit.length > 0 &&
      !setupAssistantPending ? (
        <EditIntegrationSecretModal
          key={secretsToEdit[0].id}
          selectedIntegrationSecret={secretsToEdit[0]}
          // setSelectedIntegrationSecret is called after the modal is closed, either by create
          // or skip. In either case, we don't need to change anything in the state, just move on to the
          // next secret in the queue if available
          setSelectedIntegrationSecret={handleCloseSecretsInlineModal}
          initialMode
        />
      ) : null}
      <NoPermissionModal {...noPermissionModalProps} />
      <Spacer size={30} />
    </PageSizing>
  );
};

export default MarketplacePage;
